export type LabelField = {
  x: number;
  y: number;
  font: string;
  height: number;
  width: number;
  data: string;
};

export type Side = "left" | "right" | "center";

export type QRConfig = {
  qrX?: number;
  qrY?: number;
  qrMagnification?: number;
  gap?: number;
};

export type ZplConfig = {
  dpi?: number;
  maxWidthInches?: number;
  labelHeightInches?: number;
  darkness?: number;
  maxChars?: number;
};

export type BatchItem = {
  fields: LabelField[];
  qrData?: string;
};

function trimSmart(str: string, maxChars: number) {
  if (!maxChars || str.length <= maxChars) return str;
  return str.slice(0, maxChars - 3) + "...";
}

// -----------------------------------------------
//  Generador unitaro (1 etiqueta)
export function generateZpl(
  fields: LabelField[],
  side: Side,
  qrData?: string,
  qrConfig: QRConfig = {},
  config: ZplConfig = {}
): string {
  const dpi = config.dpi ?? 203;
  const widthIn = config.maxWidthInches ?? 4.33;
  const heightIn = config.labelHeightInches ?? 1.2;

  const dotW = Math.floor(dpi * widthIn);
  const dotH = Math.floor(dpi * heightIn);

  const darkness = config.darkness ?? 15;

  let baseX: number, availW: number;

  if (side === "center") {
    baseX = 0;
    availW = dotW;
  } else {
    const halfW = dotW / 2;
    baseX = side === "right" ? halfW : 0;
    availW = halfW;
  }

  const qrMag = qrConfig.qrMagnification ?? 3;
  const qrSize = 21 * qrMag;
  const gap = qrConfig.gap ?? 10;
  const qrX = baseX + (qrConfig.qrX ?? 0);
  const qrY = qrConfig.qrY ?? 10;
  const textStartX = qrData ? qrX + qrSize + gap : baseX;

  let zpl = `^XA
^CI27
^MNW
^PW${dotW}
^LL${dotH}
^MD${darkness}
`;

  if (qrData) {
    zpl += `^FO${qrX},${qrY}
^BQN,2,${qrMag}
^FDLA,${qrData}^FS
`;
  }

  for (const f of fields) {
    const autoMax = Math.floor((availW - (textStartX - baseX)) / f.width);

    const maxChars = config.maxChars ?? autoMax;

    const txt = trimSmart(f.data, maxChars);

    zpl += `^FO${textStartX + f.x},${f.y}
^A0,${f.height},${f.width}
^FD${txt}^FS
`;
  }

  zpl += `^XZ`;
  return zpl.trim();
}

export function chunkIntoPairs<T>(arr: T[]): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += 2) {
    result.push(arr.slice(i, i + 2));
  }
  return result;
}

// -----------------------------------------------
//  Generador por lotes (2 etiquetas por zpl)
export function generateZplBatch(
  items: BatchItem[],
  qrConfig: QRConfig = {},
  config: ZplConfig = {}
): string {
  const dpi = config.dpi ?? 203;
  const maxIn = config.maxWidthInches ?? 4.33;
  const maxHtIn = config.labelHeightInches ?? 1.2;

  const totalW = Math.floor(dpi * maxIn);
  const totalH = Math.floor(dpi * maxHtIn);

  const halfW = Math.floor(totalW / 2);

  const qrMag = qrConfig.qrMagnification ?? 3;
  const qrSize = 21 * qrMag;
  const gap = qrConfig.gap ?? 10;

  const qrOffsetX = qrConfig.qrX ?? 0;
  const qrOffsetY = qrConfig.qrY ?? 10;

  const darkness = config.darkness ?? 15;

  let zpl = `^XA
^CI27
^MNW
^PW${totalW}
^LL${totalH}
^MD${darkness}
`;

  items.forEach((it, idx) => {
    const baseX = idx === 0 ? 0 : halfW;

    const qrX = baseX + qrOffsetX;
    const textStartX = qrX + (it.qrData ? qrSize + gap : 0);

    if (it.qrData) {
      zpl += `^FO${qrX},${qrOffsetY}
^BQN,2,${qrMag}
^FDLA,${it.qrData}^FS
`;
    }

    it.fields.forEach((f) => {
      const availW = halfW - (textStartX - baseX);
      const autoMax = Math.floor(availW / f.width);
      const maxChars = config.maxChars ?? autoMax;

      const txt = trimSmart(f.data, maxChars);

      zpl += `^FO${textStartX + f.x},${f.y}
^A0,${f.height},${f.width}
^FD${txt}^FS
`;
    });
  });

  zpl += `^XZ`;

  return zpl.trim();
}
